import { useEffect, useState, FormEvent } from 'react';

interface User {
  _id: string;
  username: string;
}

interface SickDayFormProps {
  userId?: string; // wenn Admin übergibt, dann vorausgewählt
  allUsers: User[];
  initialStartDate?: string;
  initialEndDate?: string;
  onClose: () => void;
  onCreate: (data: { userId: string; startDate: string; endDate: string; dayFraction?: number }) => Promise<void>;
}

function SickDayForm({
  userId,
  allUsers,
  initialStartDate = '',
  initialEndDate = '',
  onClose,
  onCreate,
}: SickDayFormProps) {
  const [selectedUser, setSelectedUser] = useState<string>(
    userId || (allUsers[0]?._id ?? '')
  );
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const [dayFraction, setDayFraction] = useState<'1' | '0.5'>('1');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const isSingleDay = !!startDate && startDate === endDate;

  useEffect(() => {
    if (!isSingleDay && dayFraction !== '1') {
      setDayFraction('1');
    }
  }, [dayFraction, isSingleDay]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!selectedUser || !startDate || !endDate) {
      setError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Startdatum darf nicht nach Enddatum liegen.');
      return;
    }

    const confirmed = window.confirm('Willst du diese Krankheitstage wirklich eintragen?');
    if (!confirmed) return;

    setError('');
    setLoading(true);
    try {
      await onCreate({
        userId: selectedUser,
        startDate,
        endDate,
        dayFraction: isSingleDay ? Number(dayFraction) : 1,
      });
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Eintragen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="card p-4 shadow-sm"
        style={{ maxWidth: 400, margin: 'auto' }}
      >
        <h4 className="mb-3">Krankheitstag(e) eintragen</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Benutzer */}
          <div className="mb-2">
            <label htmlFor="user-select" className="form-label">
              Benutzer*
            </label>
            <select
              id="user-select"
              className="form-select"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              disabled={!!userId || loading}
            >
              {!userId && <option value="">Bitte auswählen</option>}
              {allUsers.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.username}
                </option>
              ))}
            </select>
          </div>

          {/* Startdatum */}
          <div className="mb-2">
            <label htmlFor="start-date" className="form-label">
              Startdatum*
            </label>
            <input
              id="start-date"
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {/* Enddatum */}
          <div className="mb-2">
            <label htmlFor="end-date" className="form-label">
              Enddatum*
            </label>
            <input
              id="end-date"
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="sick-day-fraction" className="form-label">
              Umfang
            </label>
            <select
              id="sick-day-fraction"
              className="form-select"
              value={dayFraction}
              onChange={(e) => setDayFraction(e.target.value as '1' | '0.5')}
              disabled={loading || !isSingleDay}
            >
              <option value="1">Ganzer Tag</option>
              <option value="0.5">Halber Tag</option>
            </select>
          </div>

          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !selectedUser}
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SickDayForm;
